import { useNavigate } from "react-router-dom";
import { formatDate, formatCurrency } from "../../../utils/formatters";
import Badge from "../../common/Badge";
import { Calendar, MapPin, Ticket, TrendingUp } from "lucide-react";

const statusConfig = {
  0: { label: "Upcoming", variant: "info" },
  1: { label: "Live", variant: "success" },
  2: { label: "Past", variant: "default" },
};

const ProgressBar = ({ sold, total }) => {
  const pct = total > 0 ? Math.min((sold / total) * 100, 100) : 0;
  const color = pct >= 90 ? "bg-secondary" : "bg-primary";
  return (
    <div className="w-full bg-bg-elevated rounded-full h-1.5">
      <div
        className={`h-1.5 rounded-full ${color} transition-all`}
        style={{ width: `${pct}%` }}
      />
    </div>
  );
};

const EventCard = ({ event }) => {
  const navigate = useNavigate(); // ← must be before any early return

  if (!event) return null;

  const status = statusConfig[event.status] ?? statusConfig[0];
  const dates = event.dates?.$values ?? event.dates ?? [];
  const earliestDate = dates[0]?.date;
  const ticketsSold = event.salesStats?.tickets?.totalSold ?? 0;
  const ticketsTotal =
    event.salesStats?.tickets?.totalCapacity ??
    (event.tickets ?? []).reduce((sum, t) => sum + (t.quantity ?? 0), 0);
  const revenue = event.salesStats?.totalEarnedRevenue ?? 0;

  return (
    <div
      onClick={() => navigate(`/events/${event.id}`)}
      className="bg-bg-surface border border-border rounded-2xl overflow-hidden card-hover cursor-pointer group"
    >
      <div className="relative h-36 bg-bg-elevated overflow-hidden">
        {event.image ? (
          <img
            src={event.image}
            alt={event.title}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <Calendar size={32} className="text-border-hover" />
          </div>
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
        <div className="absolute top-3 right-3">
          <Badge label={status.label} variant={status.variant} />
        </div>
        {earliestDate && (
          <div className="absolute bottom-3 left-3 flex items-center gap-1.5 text-white text-xs">
            <Calendar size={11} />
            {formatDate(earliestDate)}
          </div>
        )}
      </div>

      <div className="p-4 space-y-3">
        <div>
          <h3 className="text-sm font-semibold text-text-primary truncate">
            {event.title}
          </h3>
          {event.address?.displayName && (
            <div className="flex items-center gap-1 text-xs text-text-muted mt-0.5">
              <MapPin size={10} />
              <span className="truncate">{event.address.displayName}</span>
            </div>
          )}
        </div>

        <div>
          <div className="flex items-center justify-between text-xs text-text-muted mb-1.5">
            <span className="flex items-center gap-1">
              <Ticket size={11} /> {ticketsSold} / {ticketsTotal} tickets
            </span>
            <span>
              {ticketsTotal > 0
                ? Math.round((ticketsSold / ticketsTotal) * 100)
                : 0}
              %
            </span>
          </div>
          <ProgressBar sold={ticketsSold} total={ticketsTotal} />
        </div>

        <div className="flex items-center justify-between pt-1 border-t border-border">
          <div className="flex items-center gap-1 text-xs text-text-muted">
            <TrendingUp size={11} />
            Revenue
          </div>
          <span className="text-sm font-semibold text-primary">
            {formatCurrency(revenue)}
          </span>
        </div>
      </div>
    </div>
  );
};

export default EventCard;
